"use client";

import { LOCALE_PREF_COOKIE, LOCALE_LANDING_COOKIE, type Locale } from './i18n';

// Sticky flag — once set, TimezoneLocaleSync never overwrites the
// preference cookie again, no matter what the timezone says.
const MANUAL_COOKIE = 'locale-manual';
const YEAR = 60 * 60 * 24 * 365;

function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string): void {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${YEAR}`;
}

function deleteCookie(name: string): void {
    document.cookie = `${name}=; path=/; max-age=0`;
}

export function hasManualLocale(): boolean {
    return getCookie(MANUAL_COOKIE) === '1';
}

/** Call from the language switcher's onClick, before navigating. */
export function setManualLocale(locale: Locale): void {
    setCookie(MANUAL_COOKIE, '1');
    setCookie(LOCALE_PREF_COOKIE, locale);
}

/**
 * Call from TimezoneLocaleSync on every mount with the freshly-computed
 * timezone match, so middleware.ts has an up-to-date cookie to redirect on
 * before the next request renders. No-op once a manual pick exists.
 */
export function setAutoLocale(locale: Locale): void {
    if (hasManualLocale()) return;
    setCookie(LOCALE_PREF_COOKIE, locale);
}

/**
 * One-shot: true if this page load followed middleware's generic
 * legacy-unprefixed → locale-prefixed redirect (see LOCALE_LANDING_COOKIE
 * in lib/i18n.ts / middleware.ts) — meaning this locale wasn't actually
 * chosen by anyone yet, so a timezone-based auto-correction is still fair
 * game. Deletes the cookie on read: false for every other request,
 * including a client-side navigation straight to an explicit locale URL.
 */
export function consumeLandingVisit(): boolean {
    const present = getCookie(LOCALE_LANDING_COOKIE) === '1';
    if (present) deleteCookie(LOCALE_LANDING_COOKIE);
    return present;
}
